"use client";

import { useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { Oeuvre } from "@/lib/diwline-data";

interface LightboxProps {
  oeuvre: Oeuvre | null;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}

/** Visionneuse royale —grand format, navigation clavier. */
export function Lightbox({ oeuvre, onClose, onPrev, onNext }: LightboxProps) {
  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && onPrev) onPrev();
      if (e.key === "ArrowRight" && onNext) onNext();
    },
    [onClose, onPrev, onNext]
  );

  useEffect(() => {
    if (oeuvre) {
      document.addEventListener("keydown", handleKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [oeuvre, handleKey]);

  return (
    <AnimatePresence>
      {oeuvre && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-10"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={oeuvre.titre}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold bg-royal/70 hover:bg-gold hover:text-royal transition-colors"
            aria-label="Fermer la visionneuse"
          >
            <X className="h-5 w-5" />
          </button>

          {onPrev && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onPrev();
              }}
              className="absolute left-3 md:left-6 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold bg-royal/70 hover:bg-gold hover:text-royal transition-colors"
              aria-label="Œuvre précédente"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {onNext && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onNext();
              }}
              className="absolute right-3 md:right-6 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold bg-royal/70 hover:bg-gold hover:text-royal transition-colors"
              aria-label="Œuvre suivante"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}

          <motion.figure
            key={oeuvre.src}
            initial={{ opacity: 0, scale: 0.94, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-5xl w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="gold-frame overflow-hidden bg-royal-brown">
              <img
                src={oeuvre.src}
                alt={oeuvre.titre}
                className="max-h-[68vh] w-full object-contain"
              />
            </div>
            <figcaption className="mt-5 text-center">
              <p className="font-elegant text-xs uppercase tracking-[0.35em] text-gold/80">
                {oeuvre.categorie}
              </p>
              <h3 className="mt-2 font-display text-xl md:text-2xl text-cream">{oeuvre.titre}</h3>
              <p className="mt-2 mx-auto max-w-2xl text-sm text-cream/70 leading-relaxed">
                {oeuvre.description}
              </p>
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
